import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { adminApi } from '@/lib/adminApi';
import {
  NOTICE_STATE_HEX, noticeState, resultHoldGuidance, runHolds, whenText, whoText,
  type HeldOrder, type ResultHold, type UnmatchedPlacement, type WaitingOrder,
} from '@/lib/pathologyOrders';
import { AdminPage, RegistryTable, Pill, EntityFormModal, LoadingNote, ErrorNote, type FieldSpec } from '@/admin';

// Route A's manual fallback (po-console-pathology-orders, over po-manual-provider).
// A person sees what is waiting, opens one order's referral form in the clinic
// checkout's field order, places it at the checkout by hand, and records
// iMedical's order id here. Core decides; this page shows. Placing and cancelling
// are refused by core while a recipe run holds a live claim (409), so the buttons
// are disabled for that order too, and core's message is shown if it refuses anyway.

type Target = { user_ref: string; order_id: string; who: string; ids_read?: string[] };

const QUEUE_KEY = ['pathology-queue'];

function errText(e: unknown): string {
  return e instanceof Error ? e.message : 'Error';
}

function Person({ o }: { o: { name?: string | null; user_ref: string } }) {
  return (
    <span>
      <span className="font-medium">{whoText(o)}</span>
      {o.name ? <span className="block font-mono text-[11px] text-text-muted">{o.user_ref}</span> : null}
    </span>
  );
}

function useReferral(target: Target) {
  return useQuery({
    queryKey: ['pathology-referral', target.user_ref, target.order_id],
    queryFn: () => adminApi.pathology.referral(target.user_ref, target.order_id),
    staleTime: 0,
  });
}

/** Copy one referral value as it is shown, for pasting into the clinic checkout
 *  (orders are placed by hand, owner 2026-10-12): never retyped. */
function CopyButton({ fieldKey, value }: { fieldKey: string; value: string | null }) {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <Button
      data-testid={`pathology-referral-copy-${fieldKey}`}
      data-copied={copied}
      variant="ghost" size="sm" disabled={value === null}
      title={value === null ? 'Not usable: nothing to copy' : undefined}
      onClick={async () => {
        if (value === null) return;
        try {
          await navigator.clipboard.writeText(value);
          setFailed(false);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          setFailed(true);
        }
      }}
    >
      {failed ? 'Copy failed' : copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

/** Who a hand placement is for, beside the form it is recorded in, so a wrong
 *  person is visible before the id is saved (what the recipe used to check). */
function RecordFor({ target }: { target: Target }) {
  const referral = useReferral(target);
  const fields = referral.data?.form.fields ?? [];
  const value = (key: string) => fields.find((f) => f.key === key)?.value ?? null;
  const name = [value('first_name'), value('last_name')].filter(Boolean).join(' ');
  const dob = value('date_of_birth');
  if (referral.isLoading) return <LoadingNote />;
  if (referral.isError || !name || !dob) {
    return (
      <p data-testid="pathology-record-for" data-known="false" className="text-body-sm text-flagged">
        Could not read who this order is for. Open the referral form before recording.
      </p>
    );
  }
  return (
    <p data-testid="pathology-record-for" data-known="true" className="text-body-sm">
      For <span className="font-medium">{name}</span>, born <span className="font-mono">{dob}</span>
    </p>
  );
}

function ReferralModal({ target, onClose }: { target: Target; onClose: () => void }) {
  const referral = useReferral(target);
  const form = referral.data?.form;
  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-overlay" onClick={onClose}>
      <div data-testid="pathology-referral" className="w-[620px] max-w-[90vw] rounded bg-surface p-21" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-h3 font-semibold">Referral form · {target.who}</h3>
        <p className="mb-13 text-body-sm text-text-secondary">
          In the clinic checkout&apos;s order and formats. Order <span className="font-mono">{target.order_id}</span>.
        </p>
        {referral.isLoading ? <LoadingNote /> : null}
        {referral.isError ? <ErrorNote>{errText(referral.error)}</ErrorNote> : null}
        {form && form.problems.length > 0 ? (
          <div data-testid="pathology-referral-problems" className="mb-13 rounded border border-border bg-warning-soft p-13">
            <p className="mb-1 text-body-sm font-semibold">Do not place this order until these are resolved with the person:</p>
            <ul className="list-disc pl-21 text-body-sm">
              {form.problems.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        ) : null}
        {form ? (
          <div className="tbl-wrap">
            <table className="tbl">
              <tbody>
                {form.fields.map((f) => (
                  <tr key={f.key} data-testid={`pathology-referral-field-${f.key}`}>
                    <td className="text-[12px] text-text-secondary">{f.label}</td>
                    <td data-testid={`pathology-referral-value-${f.key}`} data-usable={f.value !== null} className="font-mono text-[12px]">
                      {f.value ?? <span className="text-flagged">not usable</span>}
                    </td>
                    <td className="w-[1%] whitespace-nowrap text-right">
                      <CopyButton fieldKey={f.key} value={f.value} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="mt-13 flex justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

export function PathologyOrdersPage() {
  const qc = useQueryClient();
  const queue = useQuery({ queryKey: QUEUE_KEY, queryFn: () => adminApi.pathology.queue() });
  const refresh = () => void qc.invalidateQueries({ queryKey: QUEUE_KEY });

  const [referralFor, setReferralFor] = useState<Target | null>(null);
  const [recordFor, setRecordFor] = useState<Target | null>(null);
  const [recordErr, setRecordErr] = useState<string | null>(null);

  const record = useMutation({
    mutationFn: (a: { t: Target; id: string }) => adminApi.pathology.placed(a.t.user_ref, a.t.order_id, a.id),
    onSuccess: () => { setRecordFor(null); setRecordErr(null); refresh(); },
    onError: (e: unknown) => setRecordErr(errText(e)),
  });
  const cancel = useMutation({
    mutationFn: (t: Target) => adminApi.pathology.cancel(t.user_ref, t.order_id),
    onSuccess: refresh,
    onError: (e: unknown) => { alert(errText(e)); refresh(); },
  });

  const releaseResult = useMutation({
    mutationFn: (a: { h: ResultHold; accept: boolean }) => adminApi.pathology.releaseResult(a.h.user_ref, a.h.order_id, a.accept),
    onSuccess: refresh,
    onError: (e: unknown) => { alert(errText(e)); refresh(); },
  });
  const releasePrompt = (h: ResultHold) =>
    `Release the hold on ${whoText(h)}'s result? The next poll asks iMedical again and checks the report again.`;
  const acceptPrompt = (h: ResultHold) =>
    `Confirm the held report is ${whoText(h)}'s?\n\nThat exact report goes into their account on the next poll, without the name check. A different report is still checked.`;

  const recordFields: FieldSpec[] = [{
    name: 'provider_order_id', label: 'iMedical order id', type: 'text',
    hint: recordFor?.ids_read?.length
      ? `The run read ${recordFor.ids_read.join(', ')} but could not record it. Confirm against the clinic's Patients list before entering it.`
      : 'From the clinic checkout\'s confirmation, or the clinic\'s Patients list.',
  }];

  const actions = (t: Target, opts: { held: boolean; claimedUntil?: string | null }) => {
    const running = !opts.held && runHolds({ claimed_until: opts.claimedUntil });
    const why = running ? `A recipe run holds this order until ${whenText(opts.claimedUntil)}` : undefined;
    const cancelPrompt = opts.held
      ? `A run pressed Order now on ${t.who}'s order, so a paid order may exist at iMedical. Check the clinic's Patients list first.\n\nCancel this order anyway?`
      : `Cancel ${t.who}'s order ${t.order_id}?`;
    return (
      <div className="flex items-center gap-2">
        <Button data-testid={`pathology-referral-open-${t.order_id}`} variant="outline" size="sm" onClick={() => setReferralFor(t)}>Referral form</Button>
        <Button data-testid={`pathology-record-${t.order_id}`} variant="outline" size="sm" disabled={running} title={why}
          onClick={() => { setRecordErr(null); setRecordFor(t); }}>Record order id</Button>
        <Button data-testid={`pathology-cancel-${t.order_id}`} variant="destructive" size="sm" disabled={running || cancel.isPending} title={why}
          onClick={() => { if (confirm(cancelPrompt)) cancel.mutate(t); }}>Cancel</Button>
      </div>
    );
  };

  const data = queue.data;
  const notice = data?.last_notice ?? null;
  const ns = noticeState(notice);
  const state = { loading: queue.isLoading, error: queue.isError };

  return (
    <AdminPage
      title="Pathology orders"
      description="Route A's manual fallback. Place a waiting order at the clinic checkout by hand, then record iMedical's order id here. Orders a recipe run is working on cannot be placed or cancelled until its claim ends."
      actions={<Button variant="outline" size="sm" disabled={queue.isFetching} onClick={refresh}>Refresh</Button>}
    >
      <section data-testid="pathology-held" className="mb-21">
        <h2 className="mb-1 text-h3 font-semibold">Held for a person</h2>
        <p className="mb-8 text-body-sm text-text-secondary">
          A run pressed Order now and could not finish, so a paid order may exist. Check the clinic&apos;s Patients list, then record its order id or cancel.
        </p>
        <RegistryTable<HeldOrder>
          columns={[
            { key: 'who', header: 'Person', render: (o) => <Person o={o} /> },
            { key: 'panel', header: 'Panel', className: 'font-mono text-[12px]', render: (o) => o.panel || '—' },
            { key: 'held', header: 'Held since', className: 'text-[12px] text-text-secondary', render: (o) => whenText(o.held_at) },
            { key: 'ids', header: 'Order ids the run read', className: 'font-mono text-[12px]', render: (o) => (
              <span data-testid={`pathology-ids-${o.order_id}`}>{o.ids_read.length ? o.ids_read.join(', ') : <span className="text-text-muted">none read</span>}</span>
            ) },
          ]}
          rows={data?.held ?? []}
          getRowKey={(o) => o.order_id}
          count={data?.held.length}
          state={state}
          emptyText="Nothing held."
          rowActions={(o) => actions({ user_ref: o.user_ref, order_id: o.order_id, who: whoText(o), ids_read: o.ids_read }, { held: true })}
        />
      </section>

      <section data-testid="pathology-results-held" className="mb-21">
        <h2 className="mb-1 text-h3 font-semibold">Results held</h2>
        <p className="mb-8 text-body-sm text-text-secondary">
          Complete reports the results poller would not put into the account. Until one is released here, that person&apos;s results never arrive.
        </p>
        <RegistryTable<ResultHold>
          columns={[
            { key: 'who', header: 'Person', render: (h) => <Person o={h} /> },
            { key: 'panel', header: 'Panel', className: 'font-mono text-[12px]', render: (h) => h.panel || '—' },
            { key: 'provider', header: 'iMedical order id', className: 'font-mono text-[12px]', render: (h) => h.provider_order_id || '—' },
            { key: 'held', header: 'Held since', className: 'text-[12px] text-text-secondary', render: (h) => whenText(h.held_at) },
            { key: 'why', header: 'Why', className: 'text-[12px]', render: (h) => (
              <span>
                {h.reason}
                <span data-testid={`pathology-result-guidance-${h.order_id}`} data-kind={h.kind ?? 'none'}
                  className="mt-1 block text-[11px] text-text-secondary">{resultHoldGuidance(h.kind)}</span>
              </span>
            ) },
          ]}
          rows={data?.results_held ?? []}
          getRowKey={(h) => h.order_id}
          count={data?.results_held?.length ?? 0}
          state={state}
          emptyText="Nothing held."
          rowActions={(h) => (
            <div className="flex items-center gap-2">
              {h.can_accept_report ? (
                <Button data-testid={`pathology-result-accept-${h.order_id}`} variant="outline" size="sm"
                  disabled={releaseResult.isPending}
                  onClick={() => { if (confirm(acceptPrompt(h))) releaseResult.mutate({ h, accept: true }); }}>Accept report</Button>
              ) : null}
              <Button data-testid={`pathology-result-release-${h.order_id}`} variant="outline" size="sm"
                disabled={releaseResult.isPending}
                onClick={() => { if (confirm(releasePrompt(h))) releaseResult.mutate({ h, accept: false }); }}>Release</Button>
            </div>
          )}
        />
      </section>

      <section data-testid="pathology-waiting" className="mb-21">
        <h2 className="mb-8 text-h3 font-semibold">Waiting</h2>
        <RegistryTable<WaitingOrder>
          columns={[
            { key: 'who', header: 'Person', render: (o) => <Person o={o} /> },
            { key: 'panel', header: 'Panel', className: 'font-mono text-[12px]', render: (o) => o.panel || '—' },
            { key: 'requested', header: 'Requested', className: 'text-[12px] text-text-secondary', render: (o) => whenText(o.created_at) },
            { key: 'claim', header: 'Recipe run', className: 'text-[12px]', render: (o) => (
              runHolds(o)
                ? <span data-testid="pathology-claim-live"><Pill label="running" color="#F2A93B" /> <span className="text-text-secondary">until {whenText(o.claimed_until)}</span></span>
                : <span className="text-text-muted">—</span>
            ) },
          ]}
          rows={data?.waiting ?? []}
          getRowKey={(o) => o.order_id}
          count={data?.waiting.length}
          state={state}
          emptyText="Nothing waiting."
          rowActions={(o) => actions({ user_ref: o.user_ref, order_id: o.order_id, who: whoText(o) }, { held: false, claimedUntil: o.claimed_until })}
        />
      </section>

      <section data-testid="pathology-unmatched" className="mb-21">
        <h2 className="mb-1 text-h3 font-semibold">Unmatched placements</h2>
        <p className="mb-8 text-body-sm text-text-secondary">
          Each is a paid order at iMedical that a run reported and core could not apply to a record. Match it by hand; nothing on this page clears the list.
        </p>
        <RegistryTable<UnmatchedPlacement>
          columns={[
            { key: 'at', header: 'Reported', className: 'text-[12px] text-text-secondary', render: (u) => whenText(u.at) },
            { key: 'id', header: 'iMedical order id', className: 'font-mono text-[12px]', render: (u) => u.provider_order_id },
            { key: 'order', header: 'Our order', className: 'font-mono text-[12px]', render: (u) => (
              u.order_id ? <span>{u.order_id}<span className="block text-[11px] text-text-muted">{u.user_ref}</span></span> : '—'
            ) },
            { key: 'why', header: 'Why core refused it', className: 'text-[12px]', render: (u) => u.refused },
          ]}
          rows={data?.unmatched_placements ?? []}
          getRowKey={(u) => `${u.at}-${u.provider_order_id}`}
          count={data?.unmatched_placements.length}
          state={state}
          emptyText="None."
        />
      </section>

      <section data-testid="pathology-notice">
        <h2 className="mb-8 text-h3 font-semibold">Waiting-orders email</h2>
        {queue.isSuccess ? (
          <p className="text-body-sm">
            <span data-testid="pathology-notice-state" data-state={ns}><Pill label={ns === 'none' ? 'never sent' : ns} color={NOTICE_STATE_HEX[ns]} /></span>
            {notice ? (
              <span className="text-text-secondary">
                {' '}last attempt {whenText(notice.at)} · {notice.count} waiting
                {notice.error ? <span className="text-flagged"> · {notice.error}</span> : null}
              </span>
            ) : null}
          </p>
        ) : null}
      </section>

      {referralFor ? <ReferralModal target={referralFor} onClose={() => setReferralFor(null)} /> : null}

      {recordFor ? (
        <EntityFormModal
          title={`Record iMedical order id · ${recordFor.who}`}
          intro={<RecordFor target={recordFor} />}
          mode="add"
          fields={recordFields}
          initialValues={{ provider_order_id: '' }}
          error={recordErr}
          busy={record.isPending}
          onClose={() => setRecordFor(null)}
          onSubmit={(v) => record.mutate({ t: recordFor, id: String(v.provider_order_id).trim() })}
        />
      ) : null}
    </AdminPage>
  );
}
